import type { FC } from "react";
import { useContext, useEffect, useMemo } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { ProductCard as UIProductCard } from "@jti/ui/components/ProductCard";
import { ProductCardContainerContext } from "@jti/ui/components/ProductCardContainerContextProvider/ProductCardContainerContextProvider";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import type { ColorVariant } from "@/libs/commerce/products";
import { getResizedImageUrl } from "@/utils/getResizedImageUrl";

export type ProductCardProps = {
  sku: string;
  title?: string;
  description?: string;
  sanitizedDescription?: string;
  image?: { src: string; alt: string };
  colors?: ColorVariant[];
  primaryButton?: { text?: string; href?: string; enabled: boolean };
  secondaryButton?: { text: string; href: string };
  isCurrentPriceHidden?: boolean;
  isOldPriceHidden?: boolean;
  isDiscountBadgeHidden?: boolean;
  isDescriptionHidden?: boolean;
  badge?: {
    left?: string;
    right?: string;
    leftIcon?: string;
    rightIcon?: string;
  };
  isFreeTrialProduct?: boolean;
  isProductColorVariantsHidden?: boolean;
  isBundle?: boolean;
  imageFetchPriority?: "auto" | "high" | "low";
  categoryId?: string;
  categoryName?: string;
  shouldShowQuickAddToCart?: boolean;
  isOutOfStockLabelHidden?: boolean;
};

export const ProductCard: FC<ProductCardProps> = ({
  sku,
  title,
  description,
  sanitizedDescription,
  image,
  colors,
  primaryButton,
  secondaryButton,
  badge,
  categoryId,
  categoryName,
  isCurrentPriceHidden,
  isOldPriceHidden,
  isDescriptionHidden,
  isDiscountBadgeHidden,
  isFreeTrialProduct,
  isProductColorVariantsHidden,
  isBundle,
  imageFetchPriority,
  shouldShowQuickAddToCart = false,
  isOutOfStockLabelHidden,
}) => {
  const { __ } = useTranslations();
  const { hideTeaserConfigurableOptions } = useConfig();
  const {
    addToSkuList,
    products: dynamicDataProducts,
    currency,
    isProductsDataLoading,
  } = useContext(ProductCardContainerContext);

  useEffect(() => {
    addToSkuList?.(sku, { name: title, categoryName });
  }, [addToSkuList, categoryName, sku, title]);

  const productDynamicData = dynamicDataProducts?.[sku];
  const imageAlternativeText = image?.alt ?? `Image of ${title}`;

  const stockStatusLabels: Partial<Record<string, string>> = {
    OUT_OF_STOCK: __("libs.ui.out_of_stock.title"),
    AVAILABLE_IN_STORES: __("product.label.availableinstores"),
  };

  const rightBadge =
    !isOutOfStockLabelHidden && productDynamicData?.stockStatus
      ? (stockStatusLabels[productDynamicData.stockStatus] ?? "")
      : "";

  const { colorValues, colorLabels } = useMemo(() => {
    if (hideTeaserConfigurableOptions || isProductColorVariantsHidden) {
      return { colorValues: [], colorLabels: [] };
    }

    // Prefer colors from props (ColorVariant[]), fallback to dynamic data
    const source = colors && colors.length > 0 ? colors : (productDynamicData?.productData?.colorOptions ?? []);
    return {
      colorValues: source.map((color) => color.value),
      colorLabels: source.map((color) => color.label),
    };
  }, [
    colors,
    productDynamicData?.productData?.colorOptions,
    hideTeaserConfigurableOptions,
    isProductColorVariantsHidden,
  ]);

  return (
    <UIProductCard
      dataSku={sku}
      currency={currency}
      title={title || productDynamicData?.productData?.name || ""}
      description={!isDescriptionHidden ? description || productDynamicData?.productData?.description : undefined}
      sanitizedDescription={!isDescriptionHidden ? sanitizedDescription : undefined}
      image={{
        src: image?.src || getResizedImageUrl(productDynamicData?.productData?.image),
        alt: imageAlternativeText,
      }}
      colors={colorValues}
      colorLabels={colorLabels}
      primaryButton={primaryButton}
      secondaryButton={secondaryButton}
      badge={{
        left: badge?.left,
        right: badge?.right || rightBadge,
        leftIcon: badge?.leftIcon,
        rightIcon: badge?.rightIcon,
      }}
      discount={!isDiscountBadgeHidden ? productDynamicData?.discount : undefined}
      oldPrice={!isOldPriceHidden ? productDynamicData?.oldPrice : undefined}
      currentPrice={!isCurrentPriceHidden ? productDynamicData?.currentPrice : undefined}
      isLoading={isProductsDataLoading}
      isFreeTrialProduct={isFreeTrialProduct}
      isBundle={isBundle}
      imageFetchPriority={imageFetchPriority}
      categoryId={categoryId}
      categoryName={categoryName}
      shouldShowQuickAddToCart={shouldShowQuickAddToCart}
      nicotineStrength={productDynamicData?.nicotineStrength}
    />
  );
};
